import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../shared/hooks/useAuth';
import { API_CONFIG } from '../../../config/api';
import { 
  setGlobalToken,
  InvoiceBalanceItem,
  InvoiceBalanceFilter
} from '../lib/TaxInvoiceBalance';
import { loadCustomersForTaxList, Customer } from '../lib/CustomersLoadEntId';
import { getTokenInfo, logTokenInfo, setupTokens, checkAuthentication } from '../../../config/novaqToken';
import { getFormDataFromDatabase, saveFormData, deleteAllSheetData, saveSheetData, submitReport, submitReportByCustomerOid } from '../lib/TaxSendHHOAT';
import { fetchSalarySheetData, executeMoveIncomeExpense } from './TaxPostHHOAT';
import api from '../../../shared/utils/api';

// Tax Get List API function
const getTaxList = async (entId: string): Promise<any> => {
  try {
    console.log('🔍 Getting tax list for entId:', entId);
    
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/tax-get-list?entId=${entId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('novaqToken')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ Tax list response:', data);
    
    // Дэлгэрэнгүй мэдээлэл хэвлэх
    if (data.success && data.data && data.data.reportList) {
      console.log('📋 Tax Get List API-аас авсан reportList дэлгэрэнгүй:');
      console.log('  - reportList length:', data.data.reportList.length);
      data.data.reportList.forEach((report: any, index: number) => {
        console.log(`  [${index + 1}] Report ${report.taxReportCode || report.id}:`);
        console.log('    - periodYear:', report.periodYear);
        console.log('    - period:', report.period);
        console.log('    - periodName:', report.periodName);
        console.log('    - taxTypeId:', report.taxTypeId);
        console.log('    - formNo:', report.formNo);
        console.log('    - reportNo:', report.reportNo);
        console.log('    - Full report object:', report);
      });
    }
    
    return data;
  } catch (error) {
    console.error('❌ Error getting tax list:', error);
    throw error;
  }
};


const CustomersTaxInvoiceList: React.FC = () => {
  const { userAuth } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Data state
  const [taxListData, setTaxListData] = useState<InvoiceBalanceItem[]>([]);
  const [additionalTaxData, setAdditionalTaxData] = useState<InvoiceBalanceItem[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
 
  const [currentFilter, setCurrentFilter] = useState<InvoiceBalanceFilter>({
    entId: '', // Will be set from localStorage
    page: 0,
    size: 10,
    sort: 'createdDate,desc'
  });
  
  const [showStats, setShowStats] = useState(false);
  
  // Token state
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  

  // Load all customers data using new Tax Get List API
  const loadAllCustomersData = useCallback(async () => {
    try {
      console.log('🔄 Loading all customers data using Tax Get List API...');
      setLoading(true);
      setError(null);
      
      // First load customers
      const customersData = await loadCustomersForTaxList(setCustomers, setLoading, setError);
      if (customersData.length === 0) {
        console.log('❌ No customers available');
        return;
      }
      
      const allTaxData: any[] = [];
      
      // Log all EntIds before processing
      console.log('--- Processing Customer EntIds with Tax Get List API ---');
      const processingEntIdList: string[] = [];
      customersData.forEach((customer: any, index: number) => {
        const entId = customer.EntId || customer.entId;
        const entIdValue = entId || 'NULL';
        processingEntIdList.push(entIdValue);
        console.log(`Processing Customer ${index + 1} (${customer.CustomerName}): EntId = ${entIdValue}`);
      });
      console.log('📋 Processing EntId жагсаалт:', processingEntIdList);
      console.log('📊 Нийт processing EntId тоо:', processingEntIdList.length);
      console.log('📊 NULL биш processing EntId тоо:', processingEntIdList.filter(id => id !== 'NULL').length);
      console.log('-----------------------------------');
      
      // Бүх customer-ийн data авах using Tax Get List API
      for (const customer of customersData) {
        try {
          // Use EntId field from customer data only
          const entId = customer.EntId || customer.entId;
          if (!entId) {
            console.error(`❌ Customer EntId is NULL for ${customer.CustomerName}:`, customer);
            continue; // Skip this customer
          }
          console.log(`🔄 Loading tax list for customer: ${customer.CustomerName} (EntId: ${entId})`);
          
          // Use new Tax Get List API
          const taxListData = await getTaxList(entId);
          
          if (taxListData.success && taxListData.data) {
            console.log(`🔍 Tax list data for ${customer.CustomerName}:`, taxListData);
            console.log(`🔍 Tax list data structure for ${customer.CustomerName}:`, taxListData.data);
            console.log(`🔍 taxListData.data.reportList for ${customer.CustomerName}:`, taxListData.data.reportList);
            
            // Check if data has _embedded structure
            let dataArray = [];
            if (taxListData.data._embedded && taxListData.data._embedded.vActInvoiceBalances) {
              dataArray = taxListData.data._embedded.vActInvoiceBalances;
              console.log(`📊 Found _embedded.vActInvoiceBalances for ${customer.CustomerName}:`, dataArray.length, 'items');
            } else if (Array.isArray(taxListData.data)) {
              dataArray = taxListData.data;
              console.log(`📊 Found direct array for ${customer.CustomerName}:`, dataArray.length, 'items');
            } else if (taxListData.data.reportList && Array.isArray(taxListData.data.reportList)) {
              dataArray = taxListData.data.reportList;
              console.log(`📊 Found reportList for ${customer.CustomerName}:`, dataArray.length, 'items');
              console.log(`📊 reportList content for ${customer.CustomerName}:`, dataArray);
              
              // Дэлгэрэнгүй мэдээлэл хэвлэх
              console.log(`📋 ${customer.CustomerName}-ийн reportList дэлгэрэнгүй:`);
              dataArray.forEach((report: any, index: number) => {
                console.log(`  [${index + 1}] Report ${report.taxReportCode || report.id}:`);
                console.log('    - periodYear:', report.periodYear);
                console.log('    - period:', report.period);
                console.log('    - periodName:', report.periodName);
                console.log('    - taxTypeId:', report.taxTypeId);
                console.log('    - formNo:', report.formNo);
                console.log('    - reportNo:', report.reportNo);
                console.log('    - taxReportStatus:', report.taxReportStatus);
                console.log('    - taxReportStatusName:', report.taxReportStatusName);
              });
            } else {
              console.log(`⚠️ Unknown data structure for ${customer.CustomerName}:`, typeof taxListData.data, taxListData.data);
              // Try to extract reportList from the data structure
              if (taxListData.data && taxListData.data.reportList) {
                dataArray = taxListData.data.reportList;
                console.log(`📊 Extracted reportList from data structure for ${customer.CustomerName}:`, dataArray.length, 'items');
              }
            }
            
            if (dataArray.length > 0) {
              // Customer мэдээлэл нэмэх
              const customerTaxData = dataArray.map((item: any) => ({
                ...item,
                customerName: customer.CustomerName,
                customerId: customer.CustomerID,
                customerTin: customer.TinCode,
                customerOid: customer.Oid, // GUID нэмэх
                entId: customer.EntId || customer.entId // entId нэмэх
              }));
              allTaxData.push(...customerTaxData);
              console.log(`✅ Tax list data loaded for ${customer.CustomerName}: ${customerTaxData.length} items`);
              console.log(`🔍 customerTaxData for ${customer.CustomerName}:`, customerTaxData);
            } else {
              console.log(`⚠️ No tax list data items for ${customer.CustomerName}`);
            }
          } else {
            console.log(`⚠️ No tax list data for ${customer.CustomerName}:`, taxListData);
          }
          
          console.log(`✅ Tax list data loaded for customer: ${customer.CustomerName}`);
          
        } catch (error) {
          console.error(`❌ Error loading tax list data for customer ${customer.CustomerName}:`, error);
        }
      }
      
      // Бүх data-г state-д хадгалах
      console.log('🔍 All tax data before setting state:', allTaxData);
      setTaxListData(allTaxData);
      setAdditionalTaxData([]); // Tax Get List API нь бүх data-г нэг дор буцаана
      
      // Statistics тооцоолох
      const stats = {
        totalCustomers: customersData.length,
        negativeCount: allTaxData.length,
        positiveCount: 0,
        totalItems: allTaxData.length
      };
      setStats(stats);
      
      console.log('✅ All customers tax data loaded successfully:', stats);
      
    } catch (error) {
      console.error('❌ Error loading all customers tax data:', error);
      setError('Бүх customer-ийн tax data авах алдаа: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Button handlers
  const handleToggleSearch = () => {
    setShowColumnSearch(prev => !prev);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(prev => !prev);
  };

  const handleRefresh = () => {
    console.log('🔄 Сэргээх товч дарагдаж, өгөгдөл дахин ачаалж байна...');
    loadAllCustomersData();
  };

  // Load token from localStorage on component mount
  useEffect(() => {
    console.log('🔍 Loading token from localStorage...');
    
    // Token мэдээлэл авах
    const tokenInfo = getTokenInfo();
    logTokenInfo(tokenInfo);
    
    const username = localStorage.getItem('username');
    const accountantOid = localStorage.getItem('accountantOid');
    
    console.log('🔍 localStorage мэдээлэл:');
    console.log('  - username:', username || 'null');
    console.log('  - accountantOid:', accountantOid || 'null');
    
    if (tokenInfo.hasValidToken) {
      setHasToken(true);
      console.log('✅ Token loaded successfully, hasToken set to true');
      
      // accountantOid-г entId болгон тохируулах - ЭНЭ НЬ БУРУУ!
      // EntId-г customer мэдээллээс авч байх ёстой, AccountantOid-оос биш
      console.log('⚠️ WARNING: Using accountantOid as entId is incorrect!');
      console.log('⚠️ EntId should come from customer data, not AccountantOid');
      if (accountantOid) {
        setCurrentFilter(prev => ({
          ...prev,
          entId: accountantOid
        }));
        console.log('✅ accountantOid set as entId (INCORRECT):', accountantOid);
      }
      
      // TaxToken-г global-д тохируулах
      setupTokens(tokenInfo);
      if (tokenInfo.primaryToken) {
        setGlobalToken(tokenInfo.primaryToken);
      }
      
      // Load customers first, then data
      console.log('🔄 Loading customers first...');
      loadCustomersForTaxList(setCustomers, setLoading, setError, () => {
        console.log('✅ Customers loaded, now loading data...');
        // Load data after customers are loaded
        loadAllCustomersData();
      });
    } else {
      console.log('❌ No valid token found');
      setHasToken(false);
      setError('Хэрэглэгч нэвтрээгүй эсвэл token олдсонгүй. Дахин нэвтрэнэ үү.');
      // Redirect to home page
      console.log('🔄 Redirecting to home page...');
      navigate('/');
    }
  }, [navigate, loadAllCustomersData]);

  // Get current data
  const getCurrentData = () => {
    console.log('🔍 getCurrentData called - taxListData:', taxListData.length, 'additionalTaxData:', additionalTaxData.length);
    return [...taxListData, ...additionalTaxData];
  };

  // Tax Get List Data function
  const TaxGetListData = () => {
    console.log('🔍 TaxGetListData called');
    const data = getCurrentData();
    console.log('🔍 TaxGetListData returning:', data.length, 'items');
    return data;
  };

  

 


  // Table columns configuration
  const columns: SmartTableColumn[] = [
       
   
    {
        key: 'customerId',
        label: 'Харилцагчийн регистр',
        width: 150
      },
    {
        key: 'customerName',
        label: 'Харилцагчийн нэр',
        width: 150
      },    
      {
        key: 'taxReportCode',
        label: 'Тайлангийн код',
        width: 150
      },
      {
        key: 'taxTypeName',
        label: 'Тайлангийн төрөл',
        width: 100,
        render: (value: string) => {
          // Цалин, хөдөлмөрийн хөлс... гэсэн урт текстийг ХХОАТ болгож товчлох
          if (value && value.includes('Цалин, хөдөлмөрийн хөлс')) {
            return 'ХХОАТ';
          }
          // Суутган төлөгчийн аж ахуйн нэгжид... гэсэн урт текстийг Суутган ААН болгож товчлох
          if (value && value.includes('Суутган төлөгчийн аж ахуйн нэгжид')) {
            return 'Суутган ААН';
          }
          // Суутган төлөгчийн хувь хүнд олгосон... гэсэн урт текстийг Суутган Хувь хүнд олгосон болгож товчлох
          if (value && value.includes('Суутган төлөгчийн хувь хүнд олгосон')) {
            return 'Суутган Хувь хүн';
          }
          // Нийслэл хотын албан татварын тайлан... гэсэн урт текстийг НХАТ болгож товчлох
          if (value && value.includes('Нийслэл хотын албан татварын тайлан')) {
            return 'НХАТ';
          }
          return value;
         
        }
      },      
    {
      key: 'periodYear',
      label: 'Тайлант он',
      width: 100
    },
    {
      key: 'periodName',
      label: 'Тайлант үе',
      width: 120
    },
    {
        key: 'returnBeginDate',
        label: 'Тайлант үе',
        width: 120
      },
      {
        key: 'returnDueDate',
        label: 'Тайлант үе',
        width: 120
      },
      {
        key: 'taxReportStatus',
        label: 'Төлөвийн код',
        width: 120
      },
      {
        key: 'actions',
        label: 'Үйлдэл',
        width: 80,
        render: (value: any, record: any) => (
          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
            <button
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                border: '1px solid #ccc',
                backgroundColor: '#007bff', // Цэнхэр - Тайлан илгээх
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#fff'
              }}
              onClick={async () => {
                console.log('========================================');
                console.log('📋 TaxGetList-ээс авсан Record (JSON):');
                console.log('========================================');
                console.log(JSON.stringify(record, null, 2));
                console.log('========================================');
                console.log('Тайлан илгээх clicked for record:', record);
                
                try {
                  const customerOid = record.customerOid; // GUID ашиглах
                  
                  if (!customerOid) {
                    alert('Харилцагчийн мэдээлэл олдсонгүй.');
                    return;
                  }
                  
                  // taxReportCode-оос endpoint-ийг тодорхойлох
                  const taxReportCode = record.taxReportCode;
                  console.log('📋 TaxReportCode:', taxReportCode);
                  
                  let endpoint = '';
                  let paramName = '';
                  
                  // taxReportCode-оос endpoint болон параметрын нэрийг тодорхойлох
                  if (taxReportCode === 'TT-03a' || taxReportCode === 'TT-03A') {
                    endpoint = '/api/tax-report-send/tt-03a/submit-all';
                    paramName = 'customerOid'; // lowercase
                  } else if (taxReportCode === 'VSHT-01') {
                    endpoint = '/api/tax-report-send/vsht-01/submit-all';
                    paramName = 'CustomerOid'; // uppercase
                  } else if (taxReportCode === 'TT-13') {
                    endpoint = '/api/tax-report-send/tt-13/submit-all';
                    paramName = 'CustomerOid'; // uppercase
                  } else if (taxReportCode === 'TT-12') {
                    endpoint = '/api/tax-report-send/tt-12/submit-all';
                    paramName = 'CustomerOid'; // uppercase
                  } else if (taxReportCode === 'TT-11') {
                    endpoint = '/api/tax-report-send/tt-11/submit-all';
                    paramName = 'CustomerOid'; // uppercase                  
                  } else if (taxReportCode === 'TT-28') {
                    endpoint = '/api/tax-report-send/tt-28/submit-all';
                    paramName = 'CustomerOid'; // uppercase
                  } else if (taxReportCode === 'TT-02') {
                    endpoint = '/api/tax-report-send/tt-02/submit-all';
                    paramName = 'CustomerOid';
                  } else {
                    // taxReportCode таарахгүй бол alert харуулах
                    alert('Тун удахгүй илгээхэд бэлэн болно');
                    return;
                  }
                  
                  // API дуудах
                  const url = `${endpoint}?${paramName}=${customerOid}`;
                  console.log('🚀 Calling endpoint:', url);
                  
                  const response = await api.get(url);
                  
                  if (response.success) {
                    // Амжилттай илгээгдсэн эсэхийг шалгах
                    const message = response.message || '';
                    const customerName = record.customerName || 'Unknown';
                    const reportNo = record.reportNo || 'N/A';
                    const customerOid = record.customerOid;
                    const formNo = record.formNo;
                    
                    // LoginReportSend API-аас note-ийг авах функц
                    const fetchNoteAndShowAlert = async () => {
                      if (customerOid && formNo) {
                        try {
                          console.log('🔍 LoginReportSend API-аас note-ийг аваж байна...');
                          console.log(`📋 Parameters: customerOid=${customerOid}, formNo=${formNo}`);
                          const notesUrl = `/api/tax-report-send/login-report-send/notes?customerOid=${customerOid}&formNo=${formNo}`;
                          const notesResponse = await api.get(notesUrl);
                          
                          if (notesResponse.success && notesResponse.data && notesResponse.data.notes && notesResponse.data.notes.length > 0) {
                            const latestNote = notesResponse.data.notes[0];
                            if (latestNote.Note) {
                              let noteMessage = latestNote.Note;
                              
                              // Response JSON-аас мессежийг задлах
                              if (noteMessage.includes('| Response:')) {
                                try {
                                  const responsePart = noteMessage.split('| Response:')[1];
                                  if (responsePart) {
                                    const responseJson = JSON.parse(responsePart.trim());
                                    if (responseJson.message) {
                                      noteMessage = responseJson.message;
                                    }
                                  }
                                } catch (parseError) {
                                  console.warn('⚠️ Note-ийн Response JSON parse алдаа:', parseError);
                                }
                              } else if (noteMessage.includes(' - ')) {
                                const parts = noteMessage.split(' - ');
                                if (parts.length > 1) {
                                  noteMessage = parts.slice(1).join(' - ').trim();
                                  if (noteMessage.includes(' | Response:')) {
                                    noteMessage = noteMessage.split(' | Response:')[0].trim();
                                  }
                                }
                              }
                              
                              // Note мессежийг alert-д харуулах
                              if (noteMessage && noteMessage.length > 0) {
                                alert(noteMessage);
                                console.log('✅ LoginReportSend-аас note мессеж alert-д харууллаа:', noteMessage);
                              }
                            }
                          }
                        } catch (notesError) {
                          console.warn('⚠️ LoginReportSend API-аас note авахад алдаа:', notesError);
                        }
                      }
                    };
                    
                    if (message.includes('Амжилтай илгээгдсэн') || message.includes('амжилтай илгээгдсэн')) {
                      const consoleMsg = `✅ Амжилтай илгээгдсэн: ${customerName} - ReportNo: ${reportNo}`;
                      console.log(consoleMsg);
                      await fetchNoteAndShowAlert();
                      // UI-г шинэчлэх
                      setTimeout(() => {
                        loadAllCustomersData();
                      }, 1000);
                    } else if (message.includes('Илгээсэн байсан') || message.includes('илгээсэн байсан')) {
                      const consoleMsg = `⚠️ Илгээсэн байсан тул орхив: ${customerName} - ReportNo: ${reportNo}`;
                      console.log(consoleMsg);
                      await fetchNoteAndShowAlert();
                      // UI-г шинэчлэх
                      setTimeout(() => {
                        loadAllCustomersData();
                      }, 1000);
                    } else {
                      const consoleMsg = `🔄 Тайлан илгээх процесс эхэлж байна: ${customerName} - ReportNo: ${reportNo}`;
                      console.log(consoleMsg);
                      // Background процесс эхэлсэн тул хэсэг хугацааны дараа note-ийг авах
                      setTimeout(async () => {
                        await fetchNoteAndShowAlert();
                        loadAllCustomersData();
                      }, 3000);
                    }
                  } else {
                    // Алдааны мессежийг шууд харуулах
                    const errorMsg = response.message || 'Алдаа гарлаа';
                    const customerName = record.customerName || 'Unknown';
                    const reportNo = record.reportNo || 'N/A';
                    const consoleMsg = `❌ Тайлан илгээхэд алдаа: ${customerName} - ReportNo: ${reportNo} - ${errorMsg}`;
                    console.error(consoleMsg);
                    
                    // LoginReportSend API-аас note-ийг авах
                    const customerOid = record.customerOid;
                    const formNo = record.formNo;
                    if (customerOid && formNo) {
                      try {
                        const notesUrl = `/api/tax-report-send/login-report-send/notes?customerOid=${customerOid}&formNo=${formNo}`;
                        const notesResponse = await api.get(notesUrl);
                        
                        if (notesResponse.success && notesResponse.data && notesResponse.data.notes && notesResponse.data.notes.length > 0) {
                          const latestNote = notesResponse.data.notes[0];
                          if (latestNote.Note) {
                            let noteMessage = latestNote.Note;
                            
                            // Response JSON-аас мессежийг задлах
                            if (noteMessage.includes('| Response:')) {
                              try {
                                const responsePart = noteMessage.split('| Response:')[1];
                                if (responsePart) {
                                  const responseJson = JSON.parse(responsePart.trim());
                                  if (responseJson.message) {
                                    noteMessage = responseJson.message;
                                  }
                                }
                              } catch (parseError) {
                                console.warn('⚠️ Note-ийн Response JSON parse алдаа:', parseError);
                              }
                            } else if (noteMessage.includes(' - ')) {
                              const parts = noteMessage.split(' - ');
                              if (parts.length > 1) {
                                noteMessage = parts.slice(1).join(' - ').trim();
                                if (noteMessage.includes(' | Response:')) {
                                  noteMessage = noteMessage.split(' | Response:')[0].trim();
                                }
                              }
                            }
                            
                            // Note мессежийг alert-д харуулах
                            if (noteMessage && noteMessage.length > 0) {
                              alert(noteMessage);
                            } else {
                              alert(`Тайлан илгээхэд алдаа: ${errorMsg}`);
                            }
                          } else {
                            alert(`Тайлан илгээхэд алдаа: ${errorMsg}`);
                          }
                        } else {
                          alert(`Тайлан илгээхэд алдаа: ${errorMsg}`);
                        }
                      } catch (notesError) {
                        console.warn('⚠️ LoginReportSend API-аас note авахад алдаа:', notesError);
                        alert(`Тайлан илгээхэд алдаа: ${errorMsg}`);
                      }
                    } else {
                      alert(`Тайлан илгээхэд алдаа: ${errorMsg}`);
                    }
                  }
                } catch (error: any) {
                  console.error('Тайлан илгээхэд алдаа:', error);
                  
                  // LoginReportSend API-аас note-ийг авах
                  const customerOid = record.customerOid;
                  const formNo = record.formNo;
                  
                  if (customerOid && formNo) {
                    try {
                      const notesUrl = `/api/tax-report-send/login-report-send/notes?customerOid=${customerOid}&formNo=${formNo}`;
                      const notesResponse = await api.get(notesUrl);
                      
                      if (notesResponse.success && notesResponse.data && notesResponse.data.notes && notesResponse.data.notes.length > 0) {
                        const latestNote = notesResponse.data.notes[0];
                        if (latestNote.Note) {
                          let noteMessage = latestNote.Note;
                          
                          // Response JSON-аас мессежийг задлах
                          if (noteMessage.includes('| Response:')) {
                            try {
                              const responsePart = noteMessage.split('| Response:')[1];
                              if (responsePart) {
                                const responseJson = JSON.parse(responsePart.trim());
                                if (responseJson.message) {
                                  noteMessage = responseJson.message;
                                }
                              }
                            } catch (parseError) {
                              console.warn('⚠️ Note-ийн Response JSON parse алдаа:', parseError);
                            }
                          } else if (noteMessage.includes(' - ')) {
                            const parts = noteMessage.split(' - ');
                            if (parts.length > 1) {
                              noteMessage = parts.slice(1).join(' - ').trim();
                              if (noteMessage.includes(' | Response:')) {
                                noteMessage = noteMessage.split(' | Response:')[0].trim();
                              }
                            }
                          }
                          
                          // Note мессежийг alert-д харуулах
                          if (noteMessage && noteMessage.length > 0) {
                            alert(noteMessage);
                          } else {
                            alert('Тайлан илгээхэд алдаа гарлаа');
                          }
                        } else {
                          alert('Тайлан илгээхэд алдаа гарлаа');
                        }
                      } else {
                        alert('Тайлан илгээхэд алдаа гарлаа');
                      }
                    } catch (notesError) {
                      console.warn('⚠️ LoginReportSend API-аас note авахад алдаа:', notesError);
                      alert('Тайлан илгээхэд алдаа гарлаа');
                    }
                  } else {
                    alert('Тайлан илгээхэд алдаа гарлаа');
                  }
                }
              }}
              title="Тайлан илгээх"
            >
              И
            </button>
          </div>
        )
      },
      {
        key: 'taxReportStatusName',
        label: 'Төлөв',
        width: 120
      },

  ];

  // Debug log for render
  console.log('🔍 TaxGetList render - taxListData:', taxListData.length, 'additionalTaxData:', additionalTaxData.length);

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Тушаах тайлан</h1>
          
          {/* Action Buttons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}
            addable={false}
          />
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <SmartTable
              data={[...taxListData, ...additionalTaxData]}
              columns={columns}
              fetchData={() => {
                console.log('🔄 SmartTable fetchData called');
                console.log('🔄 Calling loadAllCustomersData from fetchData');
                loadAllCustomersData();
              }}
              settingsKey="tax-invoice-balance"
              rowKey="id"
              editable={false}
              showColumnSearch={showColumnSearch}
              showColumnSettings={showColumnSettings}
            />
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="fixed top-4 right-4 z-50">
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          </div>
        )}
        
        {error && (
          <div className="fixed top-4 right-4 z-50">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default CustomersTaxInvoiceList;
